import React, { useState } from "react";
import Icons from "../icons";
import { useLayoutWidth } from "../theme";

/* PARKED NAV ENTRIES — Users, Application Configuration, Scheduling,
 * Reports & Analytics.
 *
 * These four are commented out below rather than deleted, because they are
 * coming back. Nothing else was touched: MainPage still imports UsersView,
 * AppConfigView, SchedulingView and ReportsView, still passes onSelectUsers /
 * onSelectAppConfig / onSelectScheduling / onSelectReports, and still renders
 * their routes. The screens work in full — they just have no entry point right
 * now.
 *
 * To restore one, uncomment two things: its `is…Section` const in this file,
 * and its <button> block in the nav list. Both are marked RESTORE.
 *
 * Note: commented JSX is not compiled or linted, so it can drift out of date
 * (a renamed icon or handler will not raise an error until it is uncommented).
 * If one of these has been parked for a while, build once straight after
 * uncommenting it.
 */

/**
 * Left navigation rail.
 * Props:
 *  - active: current section id ("boards")
 *  - boards: array of boards to list as submenu items
 *  - subsByBoard: { [boardId]: [subcategory] } for nesting under each board
 *  - activeBoardId: currently opened board id (for highlight)
 *  - activeSubKey: currently opened sub-category key (for highlight)
 *  - onSelectBoards: show the boards list (parent click)
 *  - onOpenBoard(id): open a specific board (submenu click)
 *  - onOpenSubcategory(boardId, subKey): open a board drilled into a sub-category
 */
// When the layout is narrower than 1440px (in 16px units, the UI scale taken
// out: theme.js layoutWidth) the rail shows icons only, so the screen's columns
// keep their room. A toggle overrides that either way, and the choice is
// remembered per browser ("auto" follows the layout again).
const SIDEBAR_KEY = "cortexqa-sidebar";
const WIDE_LAYOUT = 1440;

function readSidebarPref() {
  try {
    const v = localStorage.getItem(SIDEBAR_KEY);
    return v === "expanded" || v === "collapsed" ? v : "auto";
  } catch {
    return "auto";
  }
}

export function useSidebarCollapsed() {
  const [pref, setPref] = useState(readSidebarPref);
  const wide = useLayoutWidth() >= WIDE_LAYOUT;

  const collapsed = pref === "auto" ? !wide : pref === "collapsed";
  const toggle = () => {
    // Back to "auto" when the choice matches what the layout would pick anyway.
    const next = collapsed ? (wide ? "auto" : "expanded") : wide ? "collapsed" : "auto";
    setPref(next);
    try {
      if (next === "auto") localStorage.removeItem(SIDEBAR_KEY);
      else localStorage.setItem(SIDEBAR_KEY, next);
    } catch {
      /* ignore */
    }
  };
  return [collapsed, toggle];
}

export default function Sidebar({
  active = "boards",
  boards = [],
  subsByBoard = {},
  activeBoardId = null,
  activeSubKey = null,
  onSelectDashboard,
  onSelectForYou,
  onSelectBoards,
  onOpenBoard,
  onOpenSubcategory,
  onSelectUsers,
  onSelectAppConfig,
  onSelectDatasource,
  onSelectReports,
  onSelectScheduling,
  onSelectTickets,
  onSelectRequirements,
  onSelectAppPage,
  onSelectAppPageExecutions,
  onSelectExecutions,
}) {
  const [open, setOpen] = useState(true);
  const [collapsed, toggleCollapsed] = useSidebarCollapsed();
  const itemPad = collapsed ? "justify-center px-0" : "px-3";
  const labelClass = collapsed ? "sr-only" : "flex-1 text-left";
  const isDashboardSection = active === "dashboard";
  const isForYouSection = active === "foryou";
  const isBoardsSection = active === "boards";
  // RESTORE (Users): uncomment with its button block below.
  // const isUsersSection = active === "users";
  // RESTORE (Application Configuration): uncomment with its button block below.
  // const isAppConfigSection = active === "appconfig";
  // RESTORE (Datasource): uncomment with its button block below.
  // const isDatasourceSection = active === "datasource";
  // RESTORE (Reports & Analytics): uncomment with its button block below.
  // const isReportsSection = active === "reports";
  // RESTORE (Scheduling): uncomment with its button block below.
  // const isSchedulingSection = active === "scheduling";
  // RESTORE (Jira Work Items): uncomment with its button block below.
  // const isTicketsSection = active === "tickets";
  const isRequirementsSection = active === "requirements";
  const isAppPageSection = active === "apppage";
  const isAppPageExecSection = active === "apppageExecutions";
  const isExecutionsSection = active === "executions";

  const handleParentClick = () => {
    setOpen((o) => !o);
    onSelectBoards?.();
  };

  return (
    <aside
      className={`flex flex-none flex-col border-r border-slate-200 bg-card transition-[width] ${collapsed ? "w-16" : "w-60"}`}
      data-collapsed={collapsed ? "true" : "false"}
    >
      <div className={`cq-scroll flex-1 overflow-y-auto ${collapsed ? "px-2 py-3" : "p-3"}`}>
        <div className={`flex items-center pb-2 pt-1 ${collapsed ? "justify-center" : "justify-between pl-3"}`}>
          <p
            className={[
              "text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-400",
              collapsed ? "sr-only" : "",
            ].join(" ")}
          >
            Workspace
          </p>
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-expanded={!collapsed}
            className="flex h-7 w-7 flex-none items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
          >
            {collapsed ? <Icons.ChevronRight className="h-4 w-4" /> : <Icons.ChevronLeft className="h-4 w-4" />}
          </button>
        </div>

        <nav className="space-y-1">
          {/* Dashboard */}
          <button
            type="button"
            onClick={() => onSelectDashboard?.()}
            title={collapsed ? "Dashboard" : undefined}
            className={[
              `flex w-full items-center gap-3 rounded-lg py-2 text-sm font-medium transition ${itemPad}`,
              isDashboardSection
                ? "bg-brand-50 text-brand-800 ring-1 ring-brand-600/20"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
            ].join(" ")}
          >
            <Icons.Home
              className={["h-5 w-5", isDashboardSection ? "text-brand-700" : "text-slate-400"].join(" ")}
            />
            <span className={labelClass}>Dashboard</span>
          </button>

          {/* For you — epics, stories, tests and Test Executions assigned to the
              signed-in user in Jira */}
          <button
            type="button"
            onClick={() => onSelectForYou?.()}
            title={collapsed ? "For you" : undefined}
            className={[
              `flex w-full items-center gap-3 rounded-lg py-2 text-sm font-medium transition ${itemPad}`,
              isForYouSection
                ? "bg-brand-50 text-brand-800 ring-1 ring-brand-600/20"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
            ].join(" ")}
          >
            <Icons.UserCircle
              className={["h-5 w-5", isForYouSection ? "text-brand-700" : "text-slate-400"].join(" ")}
            />
            <span className={labelClass}>For you</span>
          </button>

          {/* Requirement Board — epics › story cards (testing) › Xray & Playwright tests */}
          <button
            type="button"
            onClick={() => onSelectRequirements?.()}
            title={collapsed ? "Requirement Board" : undefined}
            className={[
              `mt-2 flex w-full items-center gap-3 rounded-lg py-2 text-sm font-medium transition ${itemPad}`,
              isRequirementsSection
                ? "bg-brand-50 text-brand-800 ring-1 ring-brand-600/20"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
            ].join(" ")}
          >
            <Icons.Layers
              className={["h-5 w-5", isRequirementsSection ? "text-brand-700" : "text-slate-400"].join(" ")}
            />
            <span className={labelClass}>Requirement Board</span>
          </button>

          {/* RESTORE (Users) — parked, not deleted. Uncomment this block and the
              isUsersSection const near the top of this file.
          <button
            type="button"
            onClick={() => onSelectUsers?.()}
            title={collapsed ? "Users" : undefined}
            className={[
              `mt-2 flex w-full items-center gap-3 rounded-lg py-2 text-sm font-medium transition ${itemPad}`,
              isUsersSection
                ? "bg-brand-50 text-brand-800 ring-1 ring-brand-600/20"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
            ].join(" ")}
          >
            <Icons.Users
              className={["h-5 w-5", isUsersSection ? "text-brand-700" : "text-slate-400"].join(" ")}
            />
            <span className={labelClass}>Users</span>
          </button>
          */}

          {/* RESTORE (Application Configuration) — parked, not deleted. Uncomment
              this block and the isAppConfigSection const near the top of this file.
          <button
            type="button"
            onClick={() => onSelectAppConfig?.()}
            title={collapsed ? "Application Configuration" : undefined}
            className={[
              `mt-2 flex w-full items-center gap-3 rounded-lg py-2 text-sm font-medium transition ${itemPad}`,
              isAppConfigSection
                ? "bg-brand-50 text-brand-800 ring-1 ring-brand-600/20"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
            ].join(" ")}
          >
            <Icons.Settings
              className={["h-5 w-5", isAppConfigSection ? "text-brand-700" : "text-slate-400"].join(" ")}
            />
            <span className={labelClass}>Application Configuration</span>
          </button>
          */}

          {/* RESTORE (Datasource) — JSON datasets that feed dynamic test-case
              values. Parked, not deleted. Uncomment this block and the
              isDatasourceSection const near the top of this file.
          <button
            type="button"
            onClick={() => onSelectDatasource?.()}
            title={collapsed ? "Datasource" : undefined}
            className={[
              `mt-2 flex w-full items-center gap-3 rounded-lg py-2 text-sm font-medium transition ${itemPad}`,
              isDatasourceSection
                ? "bg-brand-50 text-brand-800 ring-1 ring-brand-600/20"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
            ].join(" ")}
          >
            <Icons.Grid
              className={["h-5 w-5", isDatasourceSection ? "text-brand-700" : "text-slate-400"].join(" ")}
            />
            <span className={labelClass}>Datasource</span>
          </button>
          */}

          {/* RESTORE (Scheduling) — automated one-off / recurring test execution.
              Parked, not deleted. Uncomment this block and the isSchedulingSection
              const near the top of this file.
          <button
            type="button"
            onClick={() => onSelectScheduling?.()}
            title={collapsed ? "Scheduling" : undefined}
            className={[
              `mt-2 flex w-full items-center gap-3 rounded-lg py-2 text-sm font-medium transition ${itemPad}`,
              isSchedulingSection
                ? "bg-brand-50 text-brand-800 ring-1 ring-brand-600/20"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
            ].join(" ")}
          >
            <Icons.Calendar
              className={["h-5 w-5", isSchedulingSection ? "text-brand-700" : "text-slate-400"].join(" ")}
            />
            <span className={labelClass}>Scheduling</span>
          </button>
          */}

          {/* RESTORE (Jira Work Items) — browse issues from connected Atlassian
              Jira projects. Parked, not deleted. Uncomment this block and the
              isTicketsSection const near the top of this file.
          <button
            type="button"
            onClick={() => onSelectTickets?.()}
            title={collapsed ? "Jira Work Items" : undefined}
            className={[
              `mt-2 flex w-full items-center gap-3 rounded-lg py-2 text-sm font-medium transition ${itemPad}`,
              isTicketsSection
                ? "bg-brand-50 text-brand-800 ring-1 ring-brand-600/20"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
            ].join(" ")}
          >
            <Icons.List
              className={["h-5 w-5", isTicketsSection ? "text-brand-700" : "text-slate-400"].join(" ")}
            />
            <span className={labelClass}>Jira Work Items</span>
          </button>
          */}

          {/* Artifacts — epic → test → its executions (the Executions view) */}
          <button
            type="button"
            onClick={() => onSelectExecutions?.()}
            title={collapsed ? "Artifacts" : undefined}
            className={[
              `mt-2 flex w-full items-center gap-3 rounded-lg py-2 text-sm font-medium transition ${itemPad}`,
              isExecutionsSection
                ? "bg-brand-50 text-brand-800 ring-1 ring-brand-600/20"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
            ].join(" ")}
          >
            <Icons.ScrollText
              className={["h-5 w-5", isExecutionsSection ? "text-brand-700" : "text-slate-400"].join(" ")}
            />
            <span className={labelClass}>Artifacts</span>
          </button>

          {/* App Page — Cucumber scenarios generated from a dynamic App Page */}
          <button
            type="button"
            onClick={() => onSelectAppPage?.()}
            title={collapsed ? "App Pages" : undefined}
            className={[
              `mt-2 flex w-full items-center gap-3 rounded-lg py-2 text-sm font-medium transition ${itemPad}`,
              isAppPageSection
                ? "bg-brand-50 text-brand-800 ring-1 ring-brand-600/20"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
            ].join(" ")}
          >
            <Icons.Grid
              className={["h-5 w-5", isAppPageSection ? "text-brand-700" : "text-slate-400"].join(" ")}
            />
            <span className={labelClass}>App Pages</span>
          </button>
          {/* App Page › Executions — the App Page's Artifacts and their runs */}
          <div className={collapsed ? "mt-1" : "ml-5 mt-1 border-l border-slate-200 pl-2"}>
            <button
              type="button"
              onClick={() => onSelectAppPageExecutions?.()}
              title={collapsed ? "App Page Executions" : undefined}
              className={[
                `flex w-full items-center gap-2.5 rounded-lg py-1.5 text-[0.8125rem] font-medium transition ${itemPad}`,
                isAppPageExecSection
                  ? "bg-brand-50 text-brand-800 ring-1 ring-brand-600/20"
                  : "text-slate-500 hover:bg-slate-50 hover:text-slate-900",
              ].join(" ")}
            >
              <Icons.Play
                className={["h-4 w-4", isAppPageExecSection ? "text-brand-700" : "text-slate-400"].join(" ")}
              />
              <span className={labelClass}>Executions</span>
            </button>
          </div>


          {/* RESTORE (Reports & Analytics) — build a report from a hand-picked
              set of test cases. Parked, not deleted. Uncomment this block and the
              isReportsSection const near the top of this file.

              Belongs last when restored: it reads the work the entries above
              produce, and sitting below the Boards tree keeps that order intact.
          <button
            type="button"
            onClick={() => onSelectReports?.()}
            title={collapsed ? "Reports & Analytics" : undefined}
            className={[
              `mt-2 flex w-full items-center gap-3 rounded-lg py-2 text-sm font-medium transition ${itemPad}`,
              isReportsSection
                ? "bg-brand-50 text-brand-800 ring-1 ring-brand-600/20"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
            ].join(" ")}
          >
            <Icons.Chart
              className={["h-5 w-5", isReportsSection ? "text-brand-700" : "text-slate-400"].join(" ")}
            />
            <span className={labelClass}>Reports &amp; Analytics</span>
          </button>
          */}
        </nav>
      </div>

      {!collapsed && (
        <div className="flex-none border-t border-slate-200 px-4 py-3">
          <div className="flex items-center gap-2 text-xs text-slate-400">CortexQA · v0.1</div>
        </div>
      )}
    </aside>
  );
}
